import { sortBy } from 'lodash';
import moment from 'moment';

import {
  CollaborationWorkList,
  CollaborationWorkListTemplateTask,
  EmployeeCollaborationTask,
  EmployeeCollaborationTaskList,
  EmployeeCollaborationTaskStatus,
  EmployeeCollaborationWorklistStatus,
} from '~/data/collaboration';
import { Enum } from '~/data/enums';
import { Roles } from '~/data/user';
import { SelectedEmployeeData } from '~/hooks/employee/useGetSelectedEmployeeData';
import { getCurrentLanguage } from '~/localization';
import {
  WorklistTaskFormData,
  convertEditTaskDataToSubmitPayload,
} from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistTaskDialog/data';
import { fetchFromOldApi } from '~/services/Http';
import { convertToDateOrUndefined } from '~/utils/helpers/date';

import { CollabTemplateFormType } from '../AddWorklistDialog/useAddWorklistDialog';

export enum WorkListTableRowType {
  List = 'list',
  Task = 'task',
}

export type WorkListTableRow = {
  id: string;
  subject: string;
  description: string;
  link: string;
  startDate?: Date;
  dueDate?: Date;
  lastEdited?: Date;
  path: string[];
  type?: string;
  templateId?: string;
  responsible?: number;
  skipped?: boolean;
  done?: boolean;
  status?: string;
  locked: boolean;
  rowType: WorkListTableRowType;
  listData?: EmployeeCollaborationTaskList;
  taskData?: EmployeeCollaborationTask;
  templateData?: CollaborationWorkList;
};

export const workListRoleAccess = {
  canCreateWorkList: [
    Roles.HR_ADMIN,
    Roles.HR_ADMIN_LIMITED,
    Roles.HR_STAFF,
    Roles.HR_DEV,
    Roles.HR_SALARY,
    Roles.HR_PEOP,
    Roles.HR_PEOP_DEV,
    Roles.MANAGER,
  ],
};

export const convertCollaborationTaskListsToWorkListTableRows = (props: {
  employeeWorkLists: EmployeeCollaborationTaskList[];
  allWorkLists: CollaborationWorkList[];
  taskStateEnums: Enum[];
  taskTypeEnums: Enum[];
}): WorkListTableRow[] => {
  const { employeeWorkLists, allWorkLists, taskStateEnums, taskTypeEnums } =
    props;

  return employeeWorkLists
    .sort((a, b) =>
      moment(a.fEmpWorklistDueDate).diff(moment(b.fEmpWorklistDueDate)),
    )
    .map((list): WorkListTableRow[] => {
      const { fEmpWorklistTasks, ...listProps } = list;

      const {
        fEmpWorklistUid,
        fEmpWorklistStartDate,
        fEmpWorklistDueDate,
        fEmpWorklistTemplateId,
        fEmpWorklistStatus,
        fEmpWorklistIsLocked,
      } = listProps;

      const foundWorkListDetails = allWorkLists.find(
        (wl) => wl.fCollaborationTemplateId === fEmpWorklistTemplateId,
      );

      const templateType = () => {
        const enumCode = foundWorkListDetails?.fCollaborationTemplateTypeFK;

        return (
          taskTypeEnums.find((e) => e.code === enumCode)?.name ?? enumCode ?? ''
        );
      };

      const taskRows = sortBy(
        fEmpWorklistTasks
          .filter(
            (task) =>
              task.taskState.fTaskStatus !==
              EmployeeCollaborationTaskStatus.Deleted,
          )
          .map((task): WorkListTableRow => {
            const {
              fTaskId,
              taskCore: {
                fTaskAssigneeId,
                fTaskTitle,
                fTaskDescription,
                fTaskType,
                fTaskLink,
              },
              taskState: { fTaskDueDate, fTaskStatus, fTaskStartDate },
              taskAudit: { fTaskUpdatedTime, fTaskCreatedTime },
            } = task;

            const resolveLastEdited = (): Date | undefined => {
              const updatedTime = convertToDateOrUndefined(fTaskUpdatedTime);
              return updatedTime
                ? updatedTime
                : convertToDateOrUndefined(fTaskCreatedTime);
            };

            return {
              id: fTaskId,
              subject: fTaskTitle,
              description: fTaskDescription,
              link: fTaskLink ?? '',
              startDate: convertToDateOrUndefined(fTaskStartDate),
              dueDate: convertToDateOrUndefined(fTaskDueDate),
              type: fTaskType,
              skipped: fTaskStatus === EmployeeCollaborationTaskStatus.Skipped,
              done: fTaskStatus === EmployeeCollaborationTaskStatus.Completed,
              lastEdited: resolveLastEdited(),
              responsible: fTaskAssigneeId,
              path: [fEmpWorklistUid, `${fTaskId}`],
              locked: fEmpWorklistIsLocked,
              rowType: WorkListTableRowType.Task,
              taskData: task,
              templateData: foundWorkListDetails,
            };
          }),
        ['seq', 'id'],
      );

      const resolveWorklistStatus = () => {
        const taskCompletionState = `${
          taskRows.filter((t) => t.skipped || t.done).length
        }/${taskRows.length}`;

        const resolveStatus =
          taskStateEnums.find((e) => e.code === fEmpWorklistStatus)?.name ??
          fEmpWorklistStatus;

        switch (fEmpWorklistStatus) {
          case 'IN_PROGRESS':
            return `${resolveStatus} (${taskCompletionState})`;
          default:
            return resolveStatus;
        }
      };

      const listRow: WorkListTableRow = {
        id: fEmpWorklistUid,
        subject: foundWorkListDetails?.fCollaborationTemplateTitle ?? '',
        description:
          foundWorkListDetails?.fCollaborationTemplateDescription ?? '',
        link: '',
        type: templateType(),
        startDate: convertToDateOrUndefined(fEmpWorklistStartDate),
        dueDate: convertToDateOrUndefined(fEmpWorklistDueDate),
        skipped: undefined,
        done: undefined,
        status: resolveWorklistStatus(),
        lastEdited: undefined,
        path: [fEmpWorklistUid],
        locked: fEmpWorklistIsLocked,
        rowType: WorkListTableRowType.List,
        listData: list,
        templateData: foundWorkListDetails,
      };

      return [listRow, ...taskRows];
    })
    .flat();
};

export const getResponsibleEmployee = (
  task: CollaborationWorkListTemplateTask,
  empId: number,
  employeeData: SelectedEmployeeData | undefined,
  hrResponsible: number,
): number | undefined => {
  const responsibility = task.fCollaborationTemplateTaskAutomnAssignee;

  if (responsibility.startsWith('NAMED_EMP_')) {
    return Number(responsibility.match(/[0-9]+/g)) || undefined;
  }

  switch (responsibility) {
    case 'SELECTED_EMP':
      return empId || undefined;
    case 'SELECTED_EMP_MANAGER':
      return !!employeeData?.managerId && employeeData.managerId !== -1
        ? employeeData.managerId
        : undefined;
    case 'SELECTED_EMP_HR_RESP':
      return hrResponsible || undefined;
    default:
      return undefined;
  }
};

enum TemplateTaskAutomationRule {
  None = 'NONE',
  BeforeStartDate = 'BEFORE_START',
  OnStartDate = 'ON_START',
  AfterStartDate = 'AFTER_START',
  BeforeCompletionDate = 'BEFORE',
  OnCompletionDate = 'ON',
  AfterCompletionDate = 'AFTER',
}

const resolveTemplateTaskDueDateAutomationRule = (
  task: CollaborationWorkListTemplateTask,
): {
  rule: TemplateTaskAutomationRule;
  offset: number;
} => {
  const resolveDateRule = (
    offset: number,
    before: TemplateTaskAutomationRule,
    on: TemplateTaskAutomationRule,
    after: TemplateTaskAutomationRule,
  ) => {
    if (offset === -999999) {
      return {
        rule: TemplateTaskAutomationRule.None,
        offset,
      };
    } else if (offset === 0) {
      return {
        rule: on,
        offset,
      };
    }

    return {
      rule: offset < 0 ? before : after,
      offset,
    };
  };

  const startConfig = resolveDateRule(
    task.fCollaborationTemplateTaskAutomnDueDay,
    TemplateTaskAutomationRule.BeforeCompletionDate,
    TemplateTaskAutomationRule.OnCompletionDate,
    TemplateTaskAutomationRule.AfterCompletionDate,
  );
  const dueConfig = resolveDateRule(
    task.fCollaborationTemplateTaskAutomnStartDay,
    TemplateTaskAutomationRule.BeforeStartDate,
    TemplateTaskAutomationRule.OnStartDate,
    TemplateTaskAutomationRule.AfterStartDate,
  );

  return dueConfig.rule !== TemplateTaskAutomationRule.None
    ? dueConfig
    : startConfig;
};

export const convertTemplateTaskToTaskFormData = (
  task: CollaborationWorkListTemplateTask,
  templateStartDate: Date,
  templateDueDate: Date,
  loggedInUserId: number,
  empId: number,
  employeeData: SelectedEmployeeData | undefined,
  hrResponsible: number,
  templateId: number,
): WorklistTaskFormData => {
  const resolveDueDate = (): Date | undefined => {
    const automation = resolveTemplateTaskDueDateAutomationRule(task);

    if (automation.rule === TemplateTaskAutomationRule.None) {
      return undefined;
    }

    const referenceDate = moment(
      [
        TemplateTaskAutomationRule.BeforeStartDate,
        TemplateTaskAutomationRule.OnStartDate,
        TemplateTaskAutomationRule.AfterStartDate,
      ].includes(automation.rule)
        ? templateStartDate
        : templateDueDate,
    );

    return (
      automation.offset < 0
        ? referenceDate.subtract(automation.offset * -1, 'day')
        : referenceDate.add(automation.offset, 'day')
    ).toDate();
  };

  return {
    taskId: task.fCollaborationTemplateTaskId,
    seq: task.fCollaborationTemplateTaskSeq,
    title: task.fCollaborationTemplateTaskTitle,
    taskType: task.fCollaborationTemplateTaskTypeFK,
    description: task.fCollaborationTemplateTaskDescription,
    link: task.fCollaborationTemplateTaskLink,
    dueDate: resolveDueDate(),
    startDate: moment().toDate(),
    creatorEmployeeId: loggedInUserId,
    targetEmployeeId: empId,
    responsibleEmployeeId: getResponsibleEmployee(
      task,
      empId,
      employeeData,
      hrResponsible,
    ),
    templateId,
    taskStatus: EmployeeCollaborationTaskStatus.New,
    attachmentId: task.fCollaborationTemplateTaskAttachmentId,
    attachmentName: task.fCollaborationTemplateTaskAttachmentName,
    attachmentIsArchived: false,
  };
};

export const resolveNextTaskSeq = (
  tasks: (WorklistTaskFormData | EmployeeCollaborationTask)[],
) => {
  const resolveTaskSeq = (
    task: WorklistTaskFormData | EmployeeCollaborationTask,
  ): number | undefined => {
    if (task.hasOwnProperty('fTaskSeq')) {
      return (task as EmployeeCollaborationTask).fTaskSeq;
    } else if (task.hasOwnProperty('seq')) {
      return (task as WorklistTaskFormData).seq;
    } else {
      return undefined;
    }
  };

  return tasks.reduce((nextSeq, task): number => {
    const taskSeq = resolveTaskSeq(task);

    return !!taskSeq && taskSeq >= nextSeq ? taskSeq + 1 : nextSeq;
  }, tasks.length + 1);
};

export const createNewTaskFormData = (
  props: Pick<
    WorklistTaskFormData,
    'taskType' | 'creatorEmployeeId' | 'targetEmployeeId' | 'worklistId' | 'seq'
  >,
): WorklistTaskFormData => {
  const { taskType, creatorEmployeeId, targetEmployeeId, worklistId, seq } =
    props;

  return {
    taskId: undefined,
    seq,
    worklistId,
    title: '',
    taskType,
    description: '',
    link: '',
    dueDate: undefined,
    startDate: moment().toDate(),
    creatorEmployeeId,
    targetEmployeeId,
    taskStatus: EmployeeCollaborationTaskStatus.New,
    attachmentId: -1,
    attachmentName: '',
    attachmentIsArchived: false,
  };
};

export const convertTaskListToEmployeeTasks = (
  worklistUId: string,
  formData: CollabTemplateFormType,
): EmployeeCollaborationTaskList => {
  const payloadDateFormat = (date: Date) => moment(date).format('YYYY-MM-DD');

  const {
    templateId,
    startDate,
    dueDate,
    language,
    targetEmployeeId,
    listTasks,
  } = formData;

  return {
    fEmpWorklistUid: worklistUId,
    fEmpWorklistEmployeeId: targetEmployeeId,
    fEmpWorklistTemplateId: templateId ?? -1,
    fEmpWorklistStartDate: !!startDate ? payloadDateFormat(startDate) : '',
    fEmpWorklistDueDate: !!dueDate ? payloadDateFormat(dueDate) : '',
    fEmpWorklistStatus: EmployeeCollaborationWorklistStatus.New,
    fEmpWorklistLanguage: language ?? getCurrentLanguage(),
    fEmpWorklistIsLocked: false,
    fEmpWorklistTasks: listTasks.map((task) =>
      convertEditTaskDataToSubmitPayload(task, worklistUId),
    ),
  };
};

export const createEmployeeTemplateTasks = async (
  empId: number,
  data: EmployeeCollaborationTaskList,
) => {
  // Exclude taskIds when creating new worklist
  const payload = {
    ...data,
    fEmpWorklistTasks: data.fEmpWorklistTasks.map((t) => {
      const { fTaskId, ...rest } = t;
      return rest;
    }),
  };

  const api = `/json/employee/${empId}/worklist`;
  return await fetchFromOldApi(api, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then((r) => r.json());
};

export const updateEmployeeTemplateTasks = async (
  empId: number,
  data: EmployeeCollaborationTaskList,
): Promise<EmployeeCollaborationTaskList> => {
  const api = `/json/employee/${empId}/worklist/${data.fEmpWorklistUid}`;
  return await fetchFromOldApi(api, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then((r) => r.json());
};

export const submitEmployeeWorkListTask = async (
  data: EmployeeCollaborationTask,
): Promise<EmployeeCollaborationTask> => {
  const { fTaskId, ...rest } = data;

  const isExisting = !!fTaskId && fTaskId !== 'new';
  const method = isExisting ? 'PUT' : 'POST';
  const url = `/json/collab/task${isExisting ? `/${fTaskId}` : ''}`;
  const payload = isExisting ? data : rest;

  return await fetchFromOldApi(url, {
    method: method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then((r) => r.json());
};

export const deleteEmployeeWorklist = async (
  data: EmployeeCollaborationTaskList,
) => {
  const { fEmpWorklistEmployeeId, fEmpWorklistUid } = data;

  const api = `/json/employee/${fEmpWorklistEmployeeId}/worklist/${fEmpWorklistUid}`;
  return await fetchFromOldApi(api, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then((r) => r.json());
};

export const deleteEmployeeWorkListTask = async (
  data: EmployeeCollaborationTask,
) => {
  return submitEmployeeWorkListTask({
    ...data,
    taskState: {
      ...data.taskState,
      fTaskStatus: EmployeeCollaborationTaskStatus.Deleted,
    },
  });
};
